// Library Methods
import { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { HubConnection } from "@microsoft/signalr";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import blackout from "src/assets/images/live-update/blackLive.jpeg";
import { CrosshairIcon } from "src/assets/icons";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";

// Services
import { deviceCalibrateLobby } from "src/services/signalR/api";

// Contexts
import { useLiveUpdateContext } from "src/contexts/live-update/LiveUpdateContext";

// Styles
import { ModalCustomConfirm } from "src/styles/mui/muiStylesHelper";

type LiveStreamImageType = {
  src: string;
  deviceStageType: string;
  hmdInProximity: boolean;
  connection: HubConnection | null;
};

const LiveStreamImage = ({
  src,
  deviceStageType,
  hmdInProximity,
  connection,
}: LiveStreamImageType) => {
  const { t } = useTranslation();
  const { getToken } = useTokenAccess();

  const { device, isNotStarted, isOnline } = useLiveUpdateContext();

  // Recenter patient view request
  const handleDeviceCalibration = useCallback(async () => {
    if (!device?.id || !connection) return;

    try {
      const token = await getToken();
      await deviceCalibrateLobby(token, device?.id, connection);
    } catch (e) {
      console.error(e);
    }
  }, [connection, device?.id, getToken]);

  const inExternalCalibration = useMemo(
    () => deviceStageType === "ExternalCalibration",
    [deviceStageType]
  );

  const displayBlackStream = useMemo(
    () => inExternalCalibration || !hmdInProximity,
    [inExternalCalibration, hmdInProximity]
  );

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
      }}
    >
      <img
        src={displayBlackStream ? blackout : src}
        alt="stream"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          borderRadius: "4px",
        }}
      />
      {!displayBlackStream && (
        <Button
          startIcon={<CrosshairIcon height="20px" width="20px" />}
          sx={{
            position: "absolute",
            bottom: "8px",
            right: "8px",
            textTransform: "none",

            ...ModalCustomConfirm(),

            fontSize: "12px",
          }}
          disabled={isNotStarted || !isOnline}
          onClick={handleDeviceCalibration}
        >
          {t("Recenter")}
        </Button>
      )}
      {displayBlackStream && (
        <Box
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <Typography
            sx={{
              color: "#FFF",
              fontWeight: 600,
              fontSize: "16px",
              textAlign: "center",
            }}
          >
            {!hmdInProximity
              ? t("The device has been removed.")
              : inExternalCalibration
                ? t("Calibration in progress.")
                : ""}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default LiveStreamImage;
